import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: '91在线视频',
      url: 'https://www.91onlinevideo.com',
      description: '91在线视频 - 精选91精品视频在线、91精品视频在线播放等优质内容',
      email: 'contact@91onlinevideo.com',
      telephone: '400-888-9191',
    },
    {
      '@type': 'WebPage',
      name: '关于我们 - 91在线视频',
      url: 'https://www.91onlinevideo.com/about',
      description: '了解91在线视频的品牌理念、内容品质和联系方式',
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '91在线视频提供哪些类型的内容？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '91在线视频提供丰富的影视内容，包括91精品视频在线、91精品视频在线播放等精品影视资源，涵盖精选电影、热门剧集、动漫动画、综艺娱乐、纪录纪实以及短视频等多个分类。',
          },
        },
        {
          '@type': 'Question',
          name: '如何找到我想看的内容？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '您可以通过首页的搜索框输入关键词查找内容，也可以通过分类导航浏览不同类别的影视资源。',
          },
        },
        {
          '@type': 'Question',
          name: '视频播放支持哪些清晰度？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '91在线视频支持高清播放，提供流畅的在线观看体验，确保不缓冲、不卡顿的观影体验。',
          },
        },
        {
          '@type': 'Question',
          name: '是否需要注册才能观看？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '目前91在线视频所有内容均为免费观看，无需注册即可享受全部精品影视资源。',
          },
        },
      ],
    },
  ],
};

const faqs = [
  {
    question: '91在线视频提供哪些类型的内容？',
    answer: '91在线视频提供丰富的影视内容，包括91精品视频在线、91精品视频在线播放、91精品在线观看视频、91精品视频在线观看、91精彩视频在线观看等精品影视资源，涵盖精选电影、热门剧集、动漫动画、综艺娱乐、纪录纪实以及短视频等多个分类。',
  },
  {
    question: '如何找到我想看的内容？',
    answer: '您可以通过首页的搜索框输入关键词查找内容，也可以通过分类导航浏览不同类别的影视资源。每个分类页面还支持按评分、播放量和发布时间排序，方便您快速找到感兴趣的内容。',
  },
  {
    question: '视频播放支持哪些清晰度？',
    answer: '91在线视频支持高清播放，提供流畅的在线观看体验。我们的播放系统会根据您的网络状况自动调整画质，确保不缓冲、不卡顿的观影体验。',
  },
  {
    question: '是否需要注册才能观看？',
    answer: '目前91在线视频所有内容均为免费观看，无需注册即可享受全部精品影视资源。我们致力于为用户提供最便捷、最优质的在线观影服务。',
  },
  {
    question: '如何联系我们？',
    answer: '您可以通过本页面的联系表单发送信息，我们的团队会在收到信息后尽快与您取得联系。您也可以通过页面下方提供的邮箱地址直接联系我们。',
  },
  {
    question: '网站内容更新频率如何？',
    answer: '我们每天都会更新最新的影视内容，包括91精品视频在线、热门推荐、精选高分电影等。您可以收藏我们的网站，随时获取最新最热的影视资源。',
  },
];

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9n15v0bdkpk99j7gfbg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || '';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          form.reset();
          setTimeout(() => setFormStatus('idle'), 3000);
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />

      <main className="pt-16">
        <div className="pt-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
        </div>

        {/* Hero */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14 text-center">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950">
            关于 91在线视频
          </h1>
          <p className="text-sm md:text-base text-foreground-400 mt-3 max-w-2xl mx-auto">
            91在线视频致力于为用户提供91精品视频在线、91精品视频在线播放等优质内容，打造一站式高清在线观影平台
          </p>
        </section>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 lg:px-8 pb-10 md:pb-14">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/30">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 mb-4">
                  <i className="ri-lightbulb-line text-xl"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">品牌理念</h2>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  我们相信每个人都应该能够轻松获取高质量的影视内容。91在线视频汇聚了91精品视频在线、91精品视频在线播放等精品资源，以用户为中心，不断优化观影体验，让每一次点击都带来惊喜。
                </p>
              </div>

              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/30">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/10 text-accent-500 mb-4">
                  <i className="ri-star-line text-xl"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">内容品质</h2>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  我们严格筛选每一条内容，确保91精品在线观看视频、91精品视频在线观看等内容的品质。从精选电影到热门推荐，从高分佳作到经典回顾，每一部作品都经过精心挑选。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 lg:px-8 pb-10 md:pb-14">
          <div className="max-w-4xl mx-auto">
            <div className="bg-background-100 rounded-xl p-8 md:p-10 border border-background-200/30">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">10万+</div>
                  <p className="text-xs text-foreground-400 mt-1">精品内容</p>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">5000万+</div>
                  <p className="text-xs text-foreground-400 mt-1">累计播放</p>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">500万+</div>
                  <p className="text-xs text-foreground-400 mt-1">活跃用户</p>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">99.9%</div>
                  <p className="text-xs text-foreground-400 mt-1">播放流畅率</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 lg:px-8 pb-10 md:pb-14">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-400 text-center mb-8">
              有任何问题或建议？我们很乐意听取您的意见
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/30 space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-600 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/50 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-600 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-200/50 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-600 mb-1.5">主题</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/50 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-500 transition-colors"
                  placeholder="请输入主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-600 mb-1.5">留言</label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  required
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/50 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                  placeholder="请输入您的留言（最多500字）"
                />
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {formStatus === 'submitting' ? '发送中...' : '发送留言'}
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-green-500 text-center">留言发送成功！我们会尽快与您联系。</p>
              )}
              {formStatus === 'error' && formError && (
                <p className="text-sm text-red-500 text-center">{formError}</p>
              )}
            </form>

            {/* Contact Info */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-background-100 rounded-lg p-4 border border-background-200/30 text-center">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500 mx-auto mb-2">
                  <i className="ri-mail-line"></i>
                </div>
                <p className="text-xs text-foreground-400">电子邮箱</p>
                <p className="text-sm text-foreground-700 font-medium">contact@91onlinevideo.com</p>
              </div>
              <div className="bg-background-100 rounded-lg p-4 border border-background-200/30 text-center">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-500/10 text-accent-500 mx-auto mb-2">
                  <i className="ri-phone-line"></i>
                </div>
                <p className="text-xs text-foreground-400">客服电话</p>
                <p className="text-sm text-foreground-700 font-medium">400-888-9191</p>
              </div>
              <div className="bg-background-100 rounded-lg p-4 border border-background-200/30 text-center">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500/10 text-secondary-500 mx-auto mb-2">
                  <i className="ri-time-line"></i>
                </div>
                <p className="text-xs text-foreground-400">工作时间</p>
                <p className="text-sm text-foreground-700 font-medium">周一至周日 9:00-21:00</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 lg:px-8 pb-14 md:pb-20">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-2">
              常见问题
            </h2>
            <p className="text-sm text-foreground-400 text-center mb-8">
              关于91在线视频的常见疑问解答
            </p>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-100 rounded-lg border border-background-200/30 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-700 pr-4">{faq.question}</span>
                    <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line"></i>
                    </div>
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}